import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificacaoService } from 'app/services/notificacao.service';
import { EvolucaoService } from 'app/services/evolucao.service';
import { EvolucaoPostModel } from 'app/models/evolucao.model';

@Component({
    selector: 'app-evolucao-form',
    templateUrl: './evolucao-form.component.html',
    styleUrls: ['./evolucao-form.component.scss'],
    standalone: false
})
export class EvolucaoFormComponent implements OnInit {
    @Input() clienteId: number = 0;
    @Input() grupoId: number = 0;
    @Input() evolucaoId: number = 0;
    @Input() somenteLeitura: boolean = false;

    loading: boolean = false;
    evolucaoForm: FormGroup;

    quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'header': [1, 2, 3, false] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['clean']
        ]
    };

    constructor(
        private activeModal: NgbActiveModal,
        private notificacaoService: NotificacaoService,
        private evolucaoService: EvolucaoService,
        private formBuilder: FormBuilder
    ) {
        this.evolucaoForm = this.formBuilder.group({
            id: [0],
            clienteId: [0],
            grupoId: [null],
            titulo: [''],
            conteudo: [''],
            dataCriacao: [this.obterDataHoraBrasil()]
        });
    }

    async ngOnInit() {
        this.loading = true;

        this.evolucaoForm.patchValue({ clienteId: this.clienteId });

        if (this.grupoId && this.grupoId > 0) {
            this.evolucaoForm.patchValue({ grupoId: this.grupoId });
        }

        if (this.evolucaoId && this.evolucaoId !== 0) {
            await this.buscarEvolucao();
        }

        this.loading = false;
    }

    async buscarEvolucao() {
        try {
            const evolucao = await this.evolucaoService.obterPorId(this.evolucaoId);
            this.evolucaoForm.patchValue({
                id: evolucao.id,
                clienteId: evolucao.clienteId,
                titulo: evolucao.titulo,
                conteudo: evolucao.conteudo,
                dataCriacao: this.formatarDataParaInput(evolucao.dataCriacao)
            });
        } catch (err: any) {
            this.notificacaoService.mensagemErro('Erro ao carregar evolução');
        }
    }

    public cancelar() {
        this.activeModal.close(false);
    }

    async confirmar() {
        const titulo = this.evolucaoForm.value.titulo;
        const conteudo = this.evolucaoForm.value.conteudo;

        if (!titulo || titulo.trim() === '') {
            this.notificacaoService.mensagemErro('Título é obrigatório');
            return;
        }

        if (!conteudo || conteudo.trim() === '' || conteudo === '<p><br></p>') {
            this.notificacaoService.mensagemErro('Conteúdo é obrigatório');
            return;
        }

        const model: EvolucaoPostModel = {
            id: this.evolucaoForm.value.id,
            clienteId: this.evolucaoForm.value.clienteId,
            grupoId: this.evolucaoForm.value.grupoId || undefined,
            titulo: titulo.trim(),
            conteudo: conteudo,
            dataCriacao: this.evolucaoForm.value.dataCriacao
        };

        if (!this.evolucaoId || this.evolucaoId === 0)
            await this.adicionar(model);
        else
            await this.atualizar(model);
    }

    private async adicionar(model: EvolucaoPostModel) {
        this.loading = true;
        try {
            const resposta = await new Promise<any>((resolve, reject) =>
                this.evolucaoService.adicionar(model).subscribe({ next: resolve, error: reject })
            );
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(err?.error?.mensagem || 'Erro ao registrar evolução');
        } finally {
            this.loading = false;
        }
    }

    private async atualizar(model: EvolucaoPostModel) {
        this.loading = true;
        try {
            const resposta = await new Promise<any>((resolve, reject) =>
                this.evolucaoService.atualizar(model).subscribe({ next: resolve, error: reject })
            );
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(err?.error?.mensagem || 'Erro ao atualizar evolução');
        } finally {
            this.loading = false;
        }
    }

    private obterDataHoraBrasil(): string {
        const now = new Date();
        const parts = new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        }).formatToParts(now);
        const get = (type: string) => parts.find(p => p.type === type)?.value || '';
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    }

    private formatarDataParaInput(data: Date | string): string {
        const d = typeof data === 'string' ? data : new Date(data).toISOString();
        return d.substring(0, 16);
    }
}
